import { useEffect, useState } from "react";
import { useMsalAuth } from "@/hooks/useMsalAuth";
import { useNotificationWebSocket } from "@/hooks/useNotificationWebSocket";
import { useNotificationEvents } from "./NotificationEventContext";
import { useNotification } from "@/common/hooks/useNotification";
import { NotificationType } from "./Notification.type";

/**
 * Component that connects to the notification WebSocket when the user is authenticated.
 * This should be rendered inside the NotificationProvider so it can dispatch toast notifications.
 * It also emits notifications to subscribers via NotificationEventContext.
 */
export function NotificationWebSocketConnector() {
  const { getInstanceId, isLoggedIn } = useMsalAuth();
  const [instanceId, setInstanceId] = useState<string | undefined>(undefined);
  const { emit, shouldSuppressChatToasts, hasNotifiedSession, markSessionNotified } =
    useNotificationEvents();
  const { addNotification } = useNotification();

  // Get instanceId from URL on mount and when URL changes
  useEffect(() => {
    const updateInstanceId = () => {
      if (isLoggedIn) {
        const id = getInstanceId();
        setInstanceId(id || undefined);
      } else {
        setInstanceId(undefined);
      }
    };

    updateInstanceId();

    // Listen for URL changes (popstate events)
    window.addEventListener("popstate", updateInstanceId);

    // Also check periodically since React Router doesn't trigger popstate for all navigations
    const interval = setInterval(updateInstanceId, 2000);

    return () => {
      window.removeEventListener("popstate", updateInstanceId);
      clearInterval(interval);
    };
  }, [getInstanceId, isLoggedIn]);

  // Connect to notification WebSocket
  // showToast: false - we handle toast showing here to support suppression
  const { isConnected } = useNotificationWebSocket(instanceId, {
    onNotification: (notification) => {
      // Emit to subscribers so other components can react without creating new connections
      emit(notification);

      // For chat messages: show toast only for first message of each session
      // and only when not on the Inbox page
      if (notification.type === "chat_message") {
        // Extract sessionId from link format: /inbox?session={sessionId}
        const match = notification.link?.match(/session=([^&]+)/);
        const sessionId = match?.[1];

        // Show toast only for first message of session (when not suppressed)
        if (sessionId && !hasNotifiedSession(sessionId) && !shouldSuppressChatToasts()) {
          markSessionNotified(sessionId);

          addNotification({
            type: NotificationType.INFO,
            title: notification.title,
            message: notification.body,
            timeout: notification.autoHideMs || 8000,
            link: notification.link,
            linkText: notification.linkText,
          });
        }
        // All other chat messages: no toast (but emit still happened for inbox updates)
      } else {
        // Non-chat notifications always show toast (unless suppressed for other reasons)
        let notifType: NotificationType = NotificationType.INFO;
        if (notification.type === "warning" || notification.type === "escalation") {
          notifType = NotificationType.WARNING;
        }

        addNotification({
          type: notifType,
          title: notification.title,
          message: notification.body,
          timeout: notification.autoHideMs || 8000,
          link: notification.link,
          linkText: notification.linkText,
        });
      }
    },
    onConnectionChange: (connected) => {
      if (connected) {
        console.log("[NotificationConnector] Connected to notification WebSocket");
      }
    },
    showToast: false, // We handle toast showing above
  });

  // This component doesn't render anything visible
  return null;
}

export default NotificationWebSocketConnector;
